// Angular imports
import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';

// Rxjs imports
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// Service imports
import { AuthService } from '../_services/auth.service';
import { UserService } from '../_services/data-services/user.service';
import { ServerErrorService } from '../_services/shared-services/server-error.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {
    previousURL: string;

    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService
    ) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        const isLoggedIn = this.authService.isLoggedIn;

        if (!isLoggedIn && this.authService.hasFormsAuthenticationCookie) {
            return this.authService.loginUsingFormsAuthentication().pipe(
                map((success) => {
                    this.authService.updateSubjectLoginSuccess(success);
                    return this.processRouteActivation(success, route);
                }),
                catchError((error) => {
                    return of(this.processRouteActivation(isLoggedIn, route));
                })
            );
        } else if (isLoggedIn && !this.userService.getCurrentUserInfo()) {
            return this.userService.userPortalInfo().pipe(
                map((info) => {
                    this.userService.setCurrentUserInfo(info);
                    if (!!info.displayName) {
                        return this.processRouteActivation(isLoggedIn, route);
                    } else {
                        this.authService.logOut();
                        this.errorService.updateSubjectErrorMessage(
                            `You do not have access to management / board portal.`
                        );
                        return false;
                    }
                }),
                catchError((error) => {
                    return of(this.processRouteActivation(isLoggedIn, route));
                })
            );
        } else {
            return this.processRouteActivation(isLoggedIn, route);
        }
    }

    processRouteActivation(isLoggedIn: boolean, route: ActivatedRouteSnapshot): boolean {
        const isLoginForm = route.routeConfig.path.indexOf('login') !== -1;

        if (isLoggedIn && isLoginForm) {
            this.authService.updateSubjectLoginSuccess(true);
            this.router.navigate(['home']);
            return false;
        }

        if (!isLoggedIn && !isLoginForm) {
            let returnUrl = ((route as any)._routerState || {}).url;
            if (!returnUrl) {
                const paths = route.pathFromRoot
                    .filter((p) => p.url.length > 0)
                    .map((p) => p.url.map((us) => us.path).join('/'));
                if (paths.length > 0) {
                    returnUrl += `/${paths.join('/')}`;
                }
                const queryParams = route.queryParamMap.keys.map((key) => `${key}=${route.queryParamMap.get(key)}`);
                if (queryParams.length > 0) {
                    returnUrl += `?${queryParams.join('&')}`;
                }
            }
            // Redirecting won't clear app cache in case of token expiry hence loggin out
            this.authService.logOut(returnUrl);
        }

        // const isBoardMember = this.userService.isBoardMember();
        // if (isLoggedIn && isBoardMember) {
        //     window.location.href = environment.boardMemberRedirectionUrl;
        //     return false;
        // }

        return isLoggedIn || isLoginForm;
    }
}
